import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Box, Icon, Menu, MenuDivider, MenuItem, Text } from 'core/components';
import { InputGroup } from 'core/form';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';
import DirectionalButtons from 'app/views/boards/panels/ude/components/DirectionalButtons';
import { DimensionGroup, getGroupedDimensions, isDirectionalMenuItem } from 'app/stores/ude/measurementTransforms';
import { getDimensionIcon } from 'app/views/boards/panels/ude/selectorIcons';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';

const SearchWrapper = styled(Box)`
  flex-shrink: 0;

  .bp4-input {
    font-size: 13px;
  }

  .bp4-icon {
    top: 1px;
    left: 2px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

interface DimensionMenuProps {
  onSelect?: (itemName: string) => void;
  onSelectBoth?: (keys: string[]) => void;
  measurement: string | null;
  measurementHash: MeasurementHash;
  groupedDimensions?: DimensionGroup[];
  selectedDimensions?: Array<{ name: string }>;
  disabledDimensions?: Set<string>;
  appProtocols?: AppProtocolsDictionary;
  dismissOnSelect?: boolean;
}

const DimensionMenu = observer(
  ({
    onSelect,
    onSelectBoth,
    measurement,
    measurementHash,
    groupedDimensions,
    selectedDimensions = [],
    disabledDimensions = new Set<string>(),
    appProtocols,
    dismissOnSelect = false
  }: DimensionMenuProps) => {
    const [search, setSearch] = useState('');

    // Early return if no measurement
    if (!measurement) {
      return null;
    }

    // Get or compute groups (memoize to avoid recalculation)
    const groups = useMemo(
      () => groupedDimensions || getGroupedDimensions(measurement, measurementHash),
      [groupedDimensions, measurement, measurementHash]
    );

    // Determine which dimension categories to hide based on selected app protocol dimensions
    const disabledCategories = useMemo(() => {
      if (!appProtocols) {
        return null;
      }

      const appProtocolDimension = selectedDimensions.find((d) => d.name.startsWith('ktappprotocol__'));
      if (!appProtocolDimension) {
        return null;
      }

      const protocolName = appProtocolDimension.name.split('__')[1];
      const selectedProtocol = appProtocols[protocolName];
      if (!selectedProtocol) {
        return null;
      }

      if (selectedProtocol.add_to_standard_fields) {
        // Normal mode: only hide other protocol dimension groups
        const disabled = new Set<string>();
        Object.values(appProtocols).forEach((protocol) => {
          if (protocol.id !== selectedProtocol.id) {
            disabled.add(String(protocol.display_name));
          }
        });
        return { disabled, strict: false };
      }

      // Strict mode: only allow the selected protocol's dimension group
      return { disabled: new Set([String(selectedProtocol.display_name)]), strict: true };
    }, [selectedDimensions, appProtocols]);

    const filteredGroups = useMemo(() => {
      let result = groups;

      // Mark selected dimensions
      const selectedNames = new Set(selectedDimensions.map((d) => d.name));
      if (selectedNames.size > 0) {
        result = result.map((group) => ({
          ...group,
          items: group.items.map((item) => {
            if (!item.isDirectionalNode) {
              const selected = selectedNames.has(item.id);
              return selected !== item.nodeData.selected ? { ...item, nodeData: { ...item.nodeData, selected } } : item;
            }

            if (isDirectionalMenuItem(item)) {
              const { src, dst } = item.nodeData.Directions ?? {};
              const matchedKeys = [src?.key, dst?.key].filter((key): key is string => !!key && selectedNames.has(key));
              const isSelected = matchedKeys.length > 0;
              const prevData = item.nodeData as { selected?: boolean; selectedDirectionKeys?: string[] };
              const prevKeys = prevData.selectedDirectionKeys ?? [];
              const keysChanged =
                matchedKeys.length !== prevKeys.length || matchedKeys.some((key, i) => key !== prevKeys[i]);
              return isSelected !== prevData.selected || keysChanged
                ? { ...item, nodeData: { ...item.nodeData, selected: isSelected, selectedDirectionKeys: matchedKeys } }
                : item;
            }

            return item;
          })
        }));
      }

      // Apply app protocol category filtering
      if (disabledCategories) {
        result = result.filter(({ category }) => {
          if (disabledCategories.strict) {
            // Strict: only the selected protocol's category is allowed
            return disabledCategories.disabled.has(category);
          }
          // Normal: hide categories that belong to other protocols
          return !disabledCategories.disabled.has(category);
        });
      }

      if (!search.trim()) {
        return result;
      }

      const searchLower = search.toLowerCase().trim();
      return result
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => item.label.toLowerCase().includes(searchLower) || item.id.toLowerCase().includes(searchLower)
          )
        }))
        .filter((group) => group.items.length > 0);
    }, [groups, search, disabledCategories, selectedDimensions]);

    return (
      <Box width="425px" maxHeight="calc(100vh - 64px)">
        <SearchWrapper p={1} borderBottom="thin">
          <InputGroup
            type="search"
            fill
            autoFocus
            leftIcon="search"
            placeholder="Filter dimensions..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          />
        </SearchWrapper>
        <Menu p="4px" overflow="auto" minWidth="250px" maxHeight="calc(100vh - 120px)">
          {filteredGroups.length === 0 ? (
            <Text small muted p="12px" textAlign="center" display="block">
              No dimensions were found
            </Text>
          ) : (
            filteredGroups.map(({ category, items }) => (
              <React.Fragment key={category}>
                {category.trim() !== '' && category !== 'Uncategorized' && <MenuDivider title={category} />}
                {items.map((node) => {
                  const nodeKey = String(node.id);
                  const isDisabled = disabledDimensions.has(nodeKey);
                  if (isDirectionalMenuItem(node)) {
                    // Build matches array for DirectionalButtons to highlight selected directions
                    const selectedDirectionKeys =
                      (node.nodeData as { selectedDirectionKeys?: string[] })?.selectedDirectionKeys ?? [];
                    const matches = selectedDirectionKeys.map((key) => ({ name: key }));
                    const directions = node.nodeData.Directions;

                    return (
                      <MenuItem
                        key={nodeKey}
                        icon={
                          <Icon icon={getDimensionIcon(node.nodeData, node.id, category)} size={12} color="muted" />
                        }
                        text={
                          <DirectionalButtons
                            label={node.label}
                            directions={directions}
                            onSelect={onSelect}
                            matches={matches}
                            highlightSelected
                          />
                        }
                        className={node.className}
                        selected={Boolean(node.nodeData?.selected)}
                        disabled={isDisabled}
                        shouldDismissPopover={dismissOnSelect}
                        onClick={(e: React.MouseEvent) => {
                          if (onSelectBoth) {
                            const keys = Object.values(directions)
                              .map((d) => d.key)
                              .filter((k): k is string => !!k);
                            onSelectBoth(keys);
                          } else {
                            e.stopPropagation();
                          }
                        }}
                      />
                    );
                  }
                  return (
                    <MenuItem
                      key={nodeKey}
                      icon={<Icon icon={getDimensionIcon(node.nodeData, node.id, category)} size={12} color="muted" />}
                      text={
                        <Text textAlign="left" small flex={1}>
                          {node.label}
                        </Text>
                      }
                      className={node.className}
                      selected={Boolean(node.nodeData?.selected)}
                      disabled={isDisabled}
                      shouldDismissPopover={dismissOnSelect}
                      onClick={() => onSelect && onSelect(String(node.id))}
                    />
                  );
                })}
              </React.Fragment>
            ))
          )}
        </Menu>
      </Box>
    );
  }
);

export default DimensionMenu;
